import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Stethoscope, Activity, Percent, CalendarDays,
  Target, Eye, ShieldCheck,
} from "lucide-react";
import {
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#7CB342", "#558B2F", "#9CCC65", "#8896A0", "#B5BFC6"];

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="neu-card p-6 flex items-start justify-between gap-3">
      <div>
        <div className="text-sm subtle uppercase tracking-wider font-medium">{label}</div>
        <div className={`mt-3 heading text-3xl ${accent ? "text-neu-accent" : ""}`}>{value}</div>
        {sub && <div className="text-xs subtle mt-2">{sub}</div>}
      </div>
      <div className="w-11 h-11 rounded-xl bg-neu-bg flex items-center justify-center shadow-neu-inset-sm">
        <Icon className="w-5 h-5 text-neu-accent" />
      </div>
    </div>
  );
}

export default function SharedLog() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/shares/public/${token}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.data?.detail || "This share link is invalid or has been revoked."));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neu-bg p-6">
        <div className="neu-card p-10 max-w-lg text-center" data-testid="share-error">
          <Eye className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <div className="heading text-2xl text-[#3F4E5B]">Link unavailable</div>
          <p className="subtle mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neu-bg">
        <div className="neu-card p-8 subtle">Loading shared case log…</div>
      </div>
    );
  }

  const { user, cases, analytics } = data;

  return (
    <div className="min-h-screen bg-neu-bg">
      <header className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-neu-accent flex items-center justify-center shadow-lg shadow-neu-accent/30">
            <Stethoscope className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="heading text-xl leading-none">FellowLog</div>
            <div className="text-xs subtle mt-1">Shared case log</div>
          </div>
        </div>
        <span className="chip chip-green" data-testid="read-only-badge">
          <Eye className="w-3 h-3" /> Read-only
        </span>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 pb-16">
        <div className="neu-card p-6 md:p-8 mb-8" data-testid="share-user">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="text-xs subtle uppercase tracking-wider">Fellow</div>
              <div className="heading text-3xl md:text-4xl text-[#3F4E5B] mt-1">{user.name}</div>
              <p className="subtle mt-2">
                {user.specialty || "Surgical fellowship"}
                {user.fellowship_program ? ` · ${user.fellowship_program}` : ""}
                {user.fellowship_year ? ` · Year ${user.fellowship_year}` : ""}
                {user.pgy_level ? ` · ${user.pgy_level}` : ""}
              </p>
            </div>
            <div className="chip">
              <ShieldCheck className="w-3 h-3" /> Verified via FellowLog
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Stat icon={Activity} label="Total cases" value={analytics.total_cases} accent />
          <Stat icon={CalendarDays} label="Avg / month" value={analytics.avg_cases_per_month} />
          <Stat icon={Percent} label="Primary %" value={`${analytics.primary_surgeon_pct}%`} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-8">
          <div className="neu-card p-6">
            <div className="heading text-lg text-[#3F4E5B] mb-4">Cases per month</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.monthly_trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis allowDecimals={false} />
                <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
                <Legend />
                <Bar dataKey="primary" name="Primary" stackId="a" fill="#7CB342" radius={[4, 4, 0, 0]} />
                <Bar dataKey="first_assist" name="1st Assist" stackId="a" fill="#9CCC65" />
                <Bar dataKey="other" name="Other" stackId="a" fill="#B5BFC6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="neu-card p-6">
            <div className="heading text-lg text-[#3F4E5B] mb-4">Procedure categories</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.category_breakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={140} />
                <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
                <Bar dataKey="value" fill="#7CB342" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          <div className="neu-card p-6">
            <div className="heading text-lg text-[#3F4E5B] mb-4">Role distribution</div>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={analytics.role_distribution} dataKey="value" nameKey="name" outerRadius={95} label>
                  {analytics.role_distribution.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ backgroundColor: "#E4EBF1", border: "none", borderRadius: 12 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="neu-card p-6" data-testid="share-board">
            <div className="flex items-center gap-2 mb-2">
              <Target className="w-5 h-5 text-neu-accent" />
              <div className="heading text-lg text-[#3F4E5B]">Board progress</div>
            </div>
            <div className="text-sm subtle mb-3">Estimated completion: {analytics.board.eta}</div>
            <div className="heading text-3xl text-[#3F4E5B]">
              {analytics.board.current} / {analytics.board.target}
            </div>
            <div className="h-4 rounded-full bg-neu-bg shadow-neu-inset-sm overflow-hidden my-4">
              <div
                className="h-full bg-gradient-to-r from-neu-accent to-[#AED581]"
                style={{ width: `${Math.min(analytics.board.progress_pct, 100)}%` }}
              />
            </div>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="neu-inset p-3">
                <div className="text-xs subtle">Progress</div>
                <div className="heading text-lg text-neu-accent">{analytics.board.progress_pct}%</div>
              </div>
              <div className="neu-inset p-3">
                <div className="text-xs subtle">Primary</div>
                <div className="heading text-lg">{analytics.board.primary_count}</div>
              </div>
              <div className="neu-inset p-3">
                <div className="text-xs subtle">Remaining</div>
                <div className="heading text-lg">
                  {Math.max(analytics.board.target - analytics.board.current, 0)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="neu-card p-6 md:p-7 mt-8" data-testid="share-cases-table">
          <div className="heading text-lg text-[#3F4E5B] mb-4">
            All cases ({cases.length})
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left subtle text-xs uppercase tracking-wider border-b border-neu-muted/20">
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Procedure</th>
                  <th className="py-3 pr-4">Attending</th>
                  <th className="py-3 pr-4">Role</th>
                </tr>
              </thead>
              <tbody>
                {cases.map((c) => (
                  <tr key={c.id} className="border-b border-neu-muted/10 last:border-b-0">
                    <td className="py-3 pr-4 whitespace-nowrap">{c.date_of_procedure}</td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-neu-text line-clamp-1">{c.procedure_name}</div>
                      <div className="text-xs subtle">{c.procedure_category}</div>
                    </td>
                    <td className="py-3 pr-4 whitespace-nowrap">{c.attending_physician}</td>
                    <td className="py-3 pr-4">
                      <span className={`chip ${c.role_in_case === "Primary Surgeon" ? "chip-green" : ""}`}>
                        {c.role_in_case}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="text-center text-xs subtle mt-10">
          Read-only shared view · Powered by FellowLog
        </footer>
      </main>
    </div>
  );
}
